/**
 * The Emails area's marketing reads (part 2): templates, campaigns with their review, stats and
 * recipients, and the sending limits. Beside `queries-email.ts` (part 1) on file-size grounds; the same
 * rules apply: URLs live here where `api-requests.test.ts` executes them, and every read normalises its
 * answer in `select` so a missing field blanks a cell rather than breaking a screen.
 */
import { apiGet } from "@/lib/api";
import { DETAIL, LIVE_LIST } from "@/lib/query-freshness";
import type {
  Campaign,
  CampaignRecipientList,
  CampaignReview,
  CampaignStats,
  MarketingSettings,
  MarketingTemplate,
} from "@/types-email-marketing";

export const RECIPIENTS_PAGE_SIZE = 50;

const list = <T>(data: T[] | undefined): T[] =>
  Array.isArray(data) ? data : [];

export function emailTemplatesQuery(
  status: "all" | "draft" | "active" | "archived",
) {
  return {
    ...LIVE_LIST,
    queryKey: ["email", "templates", status] as const,
    select: list<MarketingTemplate>,
    queryFn: () =>
      apiGet<MarketingTemplate[]>("/api/admin/email/templates", {
        status: status === "all" ? undefined : status,
      }),
  };
}

export function emailTemplateQuery(templateId: string) {
  return {
    ...DETAIL,
    queryKey: ["email", "template", templateId] as const,
    select: (data: Partial<MarketingTemplate> | undefined) =>
      ({
        id: templateId,
        name: "",
        description: null,
        subject: "",
        preheader: null,
        topic: "offers",
        document: null,
        status: "draft",
        version: 0,
        ...data,
        versions: data?.versions ?? [],
      }) as MarketingTemplate,
    queryFn: () =>
      apiGet<MarketingTemplate>(
        `/api/admin/email/templates/${encodeURIComponent(templateId)}`,
      ),
  };
}

export function emailCampaignsQuery(params: { status: string; q: string }) {
  return {
    ...LIVE_LIST,
    queryKey: ["email", "campaigns", params.status, params.q] as const,
    select: list<Campaign>,
    queryFn: () =>
      apiGet<Campaign[]>("/api/admin/email/campaigns", {
        status: params.status || undefined,
        q: params.q || undefined,
      }),
  };
}

export function emailCampaignQuery(campaignId: string) {
  return {
    ...LIVE_LIST,
    queryKey: ["email", "campaign", campaignId] as const,
    select: (data: Partial<Campaign> | undefined) =>
      ({
        id: campaignId,
        name: "",
        templateId: null,
        subject: "",
        preheader: null,
        topic: "offers",
        document: null,
        segmentId: null,
        excludeUndelivered: true,
        status: "draft",
        version: 0,
        testSentVersion: null,
        scheduledAt: null,
        approvalRequestedBy: null,
        approvalRequestedAt: null,
        pausedReason: null,
        failedReason: null,
        ...data,
      }) as Campaign,
    queryFn: () =>
      apiGet<Campaign>(
        `/api/admin/email/campaigns/${encodeURIComponent(campaignId)}`,
      ),
  };
}

export function emailCampaignReviewQuery(campaignId: string) {
  return {
    ...LIVE_LIST,
    queryKey: ["email", "campaign", campaignId, "review"] as const,
    select: (data: Partial<CampaignReview> | undefined): CampaignReview => ({
      ok: false,
      version: 0,
      recipients: 0,
      matched: 0,
      ...data,
      items: data?.items ?? [],
      links: data?.links ?? [],
      externalLinks: data?.externalLinks ?? [],
      missingVariables: data?.missingVariables ?? [],
      sample: data?.sample ?? [],
    }),
    queryFn: () =>
      apiGet<CampaignReview>(
        `/api/admin/email/campaigns/${encodeURIComponent(campaignId)}/review`,
      ),
  };
}

export function emailCampaignStatsQuery(campaignId: string) {
  return {
    ...LIVE_LIST,
    queryKey: ["email", "campaign", campaignId, "stats"] as const,
    select: (data: Partial<CampaignStats> | undefined): CampaignStats => ({
      recipients: 0,
      sent: 0,
      failed: 0,
      suppressed: 0,
      skipped: 0,
      pending: 0,
      cancelled: 0,
      unsubscribed: 0,
      ...data,
      byStatus: data?.byStatus ?? {},
      clicked: { unique: 0, total: 0, ...data?.clicked },
      links: data?.links ?? [],
    }),
    queryFn: () =>
      apiGet<CampaignStats>(
        `/api/admin/email/campaigns/${encodeURIComponent(campaignId)}/stats`,
      ),
  };
}

export function emailCampaignRecipientsQuery(
  campaignId: string,
  params: { status: string; page: number },
) {
  return {
    ...LIVE_LIST,
    queryKey: [
      "email",
      "campaign",
      campaignId,
      "recipients",
      params.status,
      params.page,
    ] as const,
    select: (
      data: Partial<CampaignRecipientList> | undefined,
    ): CampaignRecipientList => ({
      rows: data?.rows ?? [],
      total: data?.total ?? 0,
      page: data?.page ?? params.page,
      pageSize: data?.pageSize ?? RECIPIENTS_PAGE_SIZE,
    }),
    queryFn: () =>
      apiGet<CampaignRecipientList>(
        `/api/admin/email/campaigns/${encodeURIComponent(campaignId)}/recipients`,
        {
          status: params.status || undefined,
          page: params.page,
          pageSize: RECIPIENTS_PAGE_SIZE,
        },
      ),
  };
}

export function emailSettingsQuery() {
  return {
    ...DETAIL,
    queryKey: ["email", "settings"] as const,
    select: (data: Partial<MarketingSettings> | undefined) => ({
      dailyCap: data?.dailyCap ?? 0,
      perMinute: data?.perMinute ?? 0,
    }),
    queryFn: () => apiGet<MarketingSettings>("/api/admin/email/settings"),
  };
}
